/**
 * Where a login is allowed to send you back to.
 *
 * The rule is deliberately narrow: a callback is a path on this origin and nothing else. Anything a
 * browser, a proxy or a decoder could read as some other origin is refused rather than repaired,
 * because a "fixed up" redirect target is exactly the bug this guards against.
 *
 * Imported by both the edge proxy and client components, so it must stay free of node builtins,
 * next/* and the DOM.
 */

export const CALLBACK_URL_PARAM = "callbackUrl";

/** Long enough for a dashboard URL with every filter set, short enough not to be a payload. */
const MAX_LENGTH = 2000;

/**
 * Paths that are never a destination. /login and /register would loop (this also covers /login-m,
 * the impersonation page, which renders the same component); /forgot-password, /reset-password and
 * /self-onboarding are auth/onboarding flows, not a post-login landing; /api and /_next are not
 * pages; /geo-blocked is a rewrite target, not somewhere you navigate.
 */
const FORBIDDEN_PREFIXES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/self-onboarding",
  "/tech-leaderboard",
  "/api",
  "/_next",
  "/geo-blocked",
];

/**
 * Next's cache buster on RSC fetches. A client-side navigation that gets bounced to login carries
 * `_rsc=<hash>` in req.nextUrl.search, and pinning that hash into the callback would leave the user
 * on a URL carrying a stale payload key.
 */
const VOLATILE_PARAMS = ["_rsc"];

/** \n and \r are how a redirect target turns into header smuggling; NUL and friends ride along. */
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/** Only ever used to resolve a relative path; nothing is fetched and the origin is thrown away. */
const PLACEHOLDER_ORIGIN = "https://callback.invalid";

/**
 * The same shape the proxy's company gate matches, but applied to the decoded, dot-resolved path.
 * Matching the raw string instead would let `/<companyA>/../<companyB>/x` be approved as companyA
 * and then navigate to companyB, and would miss `/%368a9fd…` entirely. Judging the normalized form
 * can only ever be stricter than the proxy, which is the safe direction to differ in.
 */
const COMPANY_PATH_PATTERN = /^\/([a-f0-9]{24})\/?.*$/;

export interface CallbackAccessIdentity {
  role?: string | null;
  companyIds?: string[] | null;
  allCompanies?: boolean | null;
}

/** Decode until the string stops changing, so `%252f%252fevil.com` is judged as `//evil.com`. */
function fullyDecode(value: string): string | null {
  let current = value;
  for (let i = 0; i < 4; i += 1) {
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      return null; // a malformed escape is not something we will guess at
    }
    if (next === current) return current;
    current = next;
  }
  return null; // absurd nesting is hostile by construction
}

/** True when the path could be read as pointing somewhere other than this origin. */
function looksLikeAnotherOrigin(path: string): boolean {
  return (
    !path.startsWith("/") ||
    path.startsWith("//") ||
    path.includes("\\") ||
    path.includes(":") || // a scheme, or a port; no route in this app has a colon in its path
    path.includes("@") // userinfo, the classic `/@evil.com` trick
  );
}

/**
 * The only way a callback value becomes trusted. Returns a same-origin `path` or `path?query`,
 * or null. Idempotent: the output is itself a valid input that sanitizes to the same string.
 */
export function sanitizeCallbackUrl(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;

  const value = raw.trim();
  if (!value || value.length > MAX_LENGTH) return null;
  if (CONTROL_CHARS.test(value)) return null;
  if (!value.startsWith("/")) return null;

  // A fragment never reaches the server and so cannot survive a login round trip; drop it rather
  // than pretend it is preserved.
  const hashIndex = value.indexOf("#");
  const withoutHash = hashIndex === -1 ? value : value.slice(0, hashIndex);

  const queryIndex = withoutHash.indexOf("?");
  const path = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? "" : withoutHash.slice(queryIndex + 1);

  // Resolve ./ and ../ segments, so every later check judges the path the browser will actually
  // request rather than the one that was typed.
  let normalizedPath: string;
  try {
    normalizedPath = new URL(path, PLACEHOLDER_ORIGIN).pathname;
  } catch {
    return null;
  }

  // The origin checks run on the path only: a query value may legitimately contain a colon or a
  // backslash, and it can never move the request to another host.
  const decodedPath = fullyDecode(normalizedPath);
  if (decodedPath === null) return null;
  // All three forms must look same-origin: the string as written, the dot-resolved form, and the
  // fully decoded form. Anything that only looks safe in one of them is refused.
  if (
    looksLikeAnotherOrigin(path) ||
    looksLikeAnotherOrigin(normalizedPath) ||
    looksLikeAnotherOrigin(decodedPath)
  ) {
    return null;
  }

  // The site root renders an empty page — next.config redirects it to /home before anything else,
  // so a callback of "/" is only ever a hand-written one, and honouring it would strand the user
  // on a blank screen instead of their dashboard.
  if (decodedPath === "/") return null;

  const lowerPath = decodedPath.toLowerCase();
  if (
    FORBIDDEN_PREFIXES.some(
      prefix =>
        lowerPath === prefix ||
        lowerPath.startsWith(`${prefix}/`) ||
        lowerPath.startsWith(`${prefix}-`),
    )
  ) {
    return null;
  }

  const cleanedQuery = query
    .split("&")
    .filter(part => part.length > 0 && !VOLATILE_PARAMS.includes(part.split("=")[0]))
    .join("&");

  return cleanedQuery ? `${normalizedPath}?${cleanedQuery}` : normalizedPath;
}

/**
 * The company a callback is scoped to.
 *
 * The query is dropped and the path is dot-resolved and decoded first, so the id returned is the
 * one the request will really carry — not the one the string happens to start with. Callers may
 * pass a raw value, so this cannot assume sanitizeCallbackUrl has already run.
 */
export function getCallbackCompanyId(url: string | null | undefined): string | null {
  if (typeof url !== "string") return null;

  const path = url.split(/[?#]/)[0];
  let normalizedPath: string;
  try {
    normalizedPath = new URL(path, PLACEHOLDER_ORIGIN).pathname;
  } catch {
    return null;
  }

  const decodedPath = fullyDecode(normalizedPath);
  if (decodedPath === null) return null;
  return COMPANY_PATH_PATTERN.exec(decodedPath)?.[1] ?? null;
}

/**
 * Mirrors the company gate in proxy.ts:
 *   userRole === "SuperAdmin" || allCompanies || userCompanyIds.includes(companyIdFromUrl)
 *
 * Keeping the two in step is the point: a callback this says yes to and the proxy says no to is a
 * login that silently ends on /home with no explanation.
 */
export function canAccessCallbackUrl(
  url: string | null | undefined,
  identity: CallbackAccessIdentity,
): boolean {
  if (!url) return false;
  const companyId = getCallbackCompanyId(url);
  if (!companyId) return true; // not company scoped: the route's own checks decide
  return (
    identity.role === "SuperAdmin" ||
    identity.allCompanies === true ||
    (identity.companyIds ?? []).includes(companyId)
  );
}

/** Builds `<loginPath>?callbackUrl=<encoded>`, or `<loginPath>` when the callback is not usable. */
export function loginUrlWithCallback(
  loginPath: string,
  callback: string | null | undefined,
): string {
  const safe = sanitizeCallbackUrl(callback);
  if (!safe) return loginPath;
  const separator = loginPath.includes("?") ? "&" : "?";
  return `${loginPath}${separator}${CALLBACK_URL_PARAM}=${encodeURIComponent(safe)}`;
}
